/* eslint-disable */

// adapted from markdown-to-quill-delta

import "worksheets/highlight"; // This must be imported before Quill
import visit from "unist-util-visit";
import unified from "unified";
import math from "remark-math";
import markdown from "remark-parse";
import parse from "rehype-dom-parse";
import { compact, get } from "lodash-es";
import { Quill } from "react-quill-new";
import { type MultilineString } from "@jupyterlab/nbformat";
import { type Parent, type Literal } from "unist";
import { isTestEnv } from "utils/env";

// All the colors names supported in Chrome (and other browsers)
const colors = {
  aliceblue: "#f0f8ff",
  antiquewhite: "#faebd7",
  aqua: "#00ffff",
  aquamarine: "#7fffd4",
  azure: "#f0ffff",
  beige: "#f5f5dc",
  bisque: "#ffe4c4",
  black: "#000000",
  blanchedalmond: "#ffebcd",
  blue: "#0000ff",
  blueviolet: "#8a2be2",
  brown: "#a52a2a",
  burlywood: "#deb887",
  cadetblue: "#5f9ea0",
  chartreuse: "#7fff00",
  chocolate: "#d2691e",
  coral: "#ff7f50",
  cornflowerblue: "#6495ed",
  cornsilk: "#fff8dc",
  crimson: "#dc143c",
  cyan: "#00ffff",
  darkblue: "#00008b",
  darkcyan: "#008b8b",
  darkgoldenrod: "#b8860b",
  darkgray: "#a9a9a9",
  darkgreen: "#006400",
  darkkhaki: "#bdb76b",
  darkmagenta: "#8b008b",
  darkolivegreen: "#556b2f",
  darkorange: "#ff8c00",
  darkorchid: "#9932cc",
  darkred: "#8b0000",
  darksalmon: "#e9967a",
  darkseagreen: "#8fbc8f",
  darkslateblue: "#483d8b",
  darkslategray: "#2f4f4f",
  darkturquoise: "#00ced1",
  darkviolet: "#9400d3",
  deeppink: "#ff1493",
  deepskyblue: "#00bfff",
  dimgray: "#696969",
  dodgerblue: "#1e90ff",
  firebrick: "#b22222",
  floralwhite: "#fffaf0",
  forestgreen: "#228b22",
  fuchsia: "#ff00ff",
  gainsboro: "#dcdcdc",
  ghostwhite: "#f8f8ff",
  gold: "#ffd700",
  goldenrod: "#daa520",
  gray: "#808080",
  green: "#008000",
  greenyellow: "#adff2f",
  honeydew: "#f0fff0",
  hotpink: "#ff69b4",
  "indianred ": "#cd5c5c",
  indigo: "#4b0082",
  ivory: "#fffff0",
  khaki: "#f0e68c",
  lavender: "#e6e6fa",
  lavenderblush: "#fff0f5",
  lawngreen: "#7cfc00",
  lemonchiffon: "#fffacd",
  lightblue: "#add8e6",
  lightcoral: "#f08080",
  lightcyan: "#e0ffff",
  lightgoldenrodyellow: "#fafad2",
  lightgrey: "#d3d3d3",
  lightgreen: "#90ee90",
  lightpink: "#ffb6c1",
  lightsalmon: "#ffa07a",
  lightseagreen: "#20b2aa",
  lightskyblue: "#87cefa",
  lightslategray: "#778899",
  lightsteelblue: "#b0c4de",
  lightyellow: "#ffffe0",
  lime: "#00ff00",
  limegreen: "#32cd32",
  linen: "#faf0e6",
  magenta: "#ff00ff",
  maroon: "#800000",
  mediumaquamarine: "#66cdaa",
  mediumblue: "#0000cd",
  mediumorchid: "#ba55d3",
  mediumpurple: "#9370d8",
  mediumseagreen: "#3cb371",
  mediumslateblue: "#7b68ee",
  mediumspringgreen: "#00fa9a",
  mediumturquoise: "#48d1cc",
  mediumvioletred: "#c71585",
  midnightblue: "#191970",
  mintcream: "#f5fffa",
  mistyrose: "#ffe4e1",
  moccasin: "#ffe4b5",
  navajowhite: "#ffdead",
  navy: "#000080",
  oldlace: "#fdf5e6",
  olive: "#808000",
  olivedrab: "#6b8e23",
  orange: "#ffa500",
  orangered: "#ff4500",
  orchid: "#da70d6",
  palegoldenrod: "#eee8aa",
  palegreen: "#98fb98",
  paleturquoise: "#afeeee",
  palevioletred: "#d87093",
  papayawhip: "#ffefd5",
  peachpuff: "#ffdab9",
  peru: "#cd853f",
  pink: "#ffc0cb",
  plum: "#dda0dd",
  powderblue: "#b0e0e6",
  purple: "#800080",
  rebeccapurple: "#663399",
  red: "#ff0000",
  rosybrown: "#bc8f8f",
  royalblue: "#4169e1",
  saddlebrown: "#8b4513",
  salmon: "#fa8072",
  sandybrown: "#f4a460",
  seagreen: "#2e8b57",
  seashell: "#fff5ee",
  sienna: "#a0522d",
  silver: "#c0c0c0",
  skyblue: "#87ceeb",
  slateblue: "#6a5acd",
  slategray: "#708090",
  snow: "#fffafa",
  springgreen: "#00ff7f",
  steelblue: "#4682b4",
  tan: "#d2b48c",
  teal: "#008080",
  thistle: "#d8bfd8",
  tomato: "#ff6347",
  turquoise: "#40e0d0",
  violet: "#ee82ee",
  wheat: "#f5deb3",
  white: "#ffffff",
  whitesmoke: "#f5f5f5",
  yellow: "#ffff00",
  yellowgreen: "#9acd32",
};

// It's not possible to load Quill in Node with the test runner since we are missing
// document, MutationObserver, etc., so we skip this import during test runs.
// This means that we cannot do unit tests around the HTML specific parsing code (unless
// we mock the return from Quill).
let tmpQuill: InstanceType<typeof Quill> = null;
if (!isTestEnv) {
  const tempCont = document.createElement("div");
  // This line requires Quill to find a peer dependency "highlight.js", which we
  // inject into the window object in the highlight.js file.
  tmpQuill = new Quill(tempCont);
}

const convertMarkdownToDelta = (md: MultilineString) => {
  let COLOR;

  const processor = unified().use(markdown).use(math);
  const htmlProcessor = unified().use(parse);
  const tree = processor.parse(md) as Parent;
  const ops = [];
  const addNewline = () => ops.push({ insert: "\n" });
  const flatten = (arr) => arr.reduce((flat, next) => flat.concat(next), []);
  const listVisitor = (node) => {
    if (node.ordered && node.start !== 1) {
      console.warn("Quill-Delta numbered lists must start from 1.", node);
    }
    visit(node, "listItem", listItemVisitor(node));
  };

  const listItemVisitor = (listNode) => (node) => {
    for (const child of node.children) {
      if (child.type === "paragraph") {
        paragraphVisitor()(child);
      }
      let listAttribute = "";
      if (listNode.ordered) {
        listAttribute = "ordered";
      } else if (node.checked) {
        listAttribute = "checked";
      } else if (node.checked === false) {
        listAttribute = "unchecked";
      } else {
        listAttribute = "bullet";
      }
      if (child.type === "paragraph") {
        ops.push({
          insert: "\n",
          attributes: { list: listAttribute },
        });
      }
    }
  };
  const paragraphVisitor =
    (initialOp = {}) =>
    (node) => {
      const { children } = node;
      const visitNode = (node, op) => {
        if (node.type === "text" && node.value.length > 0) {
          op = Object.assign({}, op, { insert: node.value });
        } else if (node.type === "strong") {
          op = Object.assign({}, op, {
            attributes: Object.assign({}, op.attributes, { bold: true }),
          });
          return visitChildren(node, op);
        } else if (node.type === "emphasis") {
          op = Object.assign({}, op, {
            attributes: Object.assign({}, op.attributes, { italic: true }),
          });
          return visitChildren(node, op);
        } else if (node.type === "delete") {
          op = Object.assign({}, op, {
            attributes: Object.assign({}, op.attributes, { strike: true }),
          });
          return visitChildren(node, op);
        } else if (node.type === "image") {
          op = { insert: { image: node.url } };
          if (node.alt) {
            op = Object.assign({}, op, { attributes: { alt: node.alt } });
          }
        } else if (node.type === "link") {
          const text = visitChildren(node, op);
          op = Object.assign({}, text, {
            attributes: Object.assign({}, op.attributes, { link: node.url }),
          });
        } else if (node.type === "inlineCode") {
          op = {
            insert: node.value,
            attributes: Object.assign({}, op.attributes, { font: "monospace" }),
          };
        } else if (node.type === "inlineMath") {
          op = {
            insert: { formula: node.value },
          };
        } else if (node.type === "break") {
          op = {
            insert: "\n",
          };
        } else if (node.type === "linkReference") {
          op = {
            insert: node.label,
          };
        } else if (node.type === "html" && node.value.startsWith("<font")) {
          const tree = htmlProcessor.parse(node.value) as Parent;
          const color = get(tree, "children[0].properties.color");
          if (color) {
            COLOR = colors[color];
          }
        } else if (node.type === "html" && node.value.startsWith("</font")) {
          COLOR = undefined;
        } else {
          console.warn("Unsupported node type", node, op);
          op = undefined;
        }
        if (COLOR && op) {
          op = Object.assign({}, op, { attributes: { color: COLOR } });
        }
        if (op && op.insert) {
          return op;
        }
      };
      const visitChildren = (node, op) => {
        const { children } = node;
        const ops = children.map((child) => visitNode(child, op));
        return ops.length === 1 ? ops[0] : ops;
      };

      for (const child of children) {
        const localOps = visitNode(child, initialOp);
        if (localOps instanceof Array) {
          flatten(localOps).forEach((op) => ops.push(op));
        } else {
          ops.push(localOps);
        }
      }
    };
  const headingVisitor = (node) => {
    const mapSize = (depth) => {
      switch (depth) {
        case 1:
          return "huge";
        default:
          return "large";
      }
    };
    const size = mapSize(node.depth);
    paragraphVisitor({ attributes: { size: size } })(node);
  };

  const blockquoteVisitor = (child) => {
    let newLines = false;
    child.children.forEach((x) => {
      if (x.type === "blockquote") {
        blockquoteVisitor(x);
      } else if (x.type === "paragraph") {
        newLines = true;
        paragraphVisitor({ attributes: { blockquote: true } })(x);
        ops.push({
          insert: "\n",
          attributes: { blockquote: true },
        });
      } else if (x.type === "list") {
        listVisitor({ ...x, attributes: { blockquote: true } });
        addNewline();
      }
    });
    if (newLines) {
      addNewline();
    }
  };

  for (let idx = 0; idx < tree.children.length; idx++) {
    const child = tree.children[idx];
    const nextType = idx + 1 < tree.children.length ? tree.children[idx + 1].type : "lastOne";
    if (child.type === "paragraph") {
      paragraphVisitor()(child);
      if (nextType === "paragraph" || nextType === "code" || nextType === "heading") {
        addNewline();
        addNewline();
      } else if (nextType === "lastOne" || nextType === "list") {
        addNewline();
      }
    } else if (child.type === "list") {
      listVisitor(child);
      if (nextType === "list") {
        addNewline();
      }
    } else if (child.type === "code") {
      ops.push({ insert: (child as Literal).value, attributes: { "code-block": true } });
      ops.push({ insert: "\n", attributes: { "code-block": true } });
      if (nextType === "paragraph" || nextType === "lastOne") {
        addNewline();
      }
    } else if (child.type === "math") {
      ops.push({
        insert: {
          largeFormula: (child as Literal).value,
        },
      });
      addNewline();
    } else if (child.type === "heading") {
      headingVisitor(child);
      addNewline();
    } else if (child.type === "blockquote") {
      blockquoteVisitor(child);
    } else if (child.type === "html") {
      const lines = ((child as Literal).value as string).split("\n");
      lines.forEach((line) => {
        if (!tmpQuill) return;
        const delta = tmpQuill.clipboard.convert({ text: line });
        if (delta.ops.length > 0) {
          const last_element = delta.ops[delta.ops.length - 1];
          last_element.insert += "\n";
          ops.push(...delta.ops);
        }
      });
    } else {
      console.warn("Unsupported child type", child);
    }
  }
  return compact(ops);
};

// try to convert properly, fall back to just displaying the markdown
export const markdownToDelta = (text: MultilineString) => {
  const stringified = typeof text === "string" ? text : text.join("");

  // Our parser does not recognize formulas with whitespace between $$ and $$,
  // and this is frequent in the Jupyter Notebooks we are using, therefore we
  // remove this space before sending it for parsing.
  // To make sure single-line formulas are rendered correctly, we also need to detect
  // them and insert an \n before the closing $$.
  const cleanedUp = stringified.replace(
    /\$\$(\s*)(.+?)(\s*)\$\$/g,
    (_, _2, match) => `\n\n$$${match.trim()}\n$$\n`,
  );
  // .replace(/^\s*\$\$(.+?)\$\$\s$/gm, '$$$$$1\n$$$$');

  let delta;
  try {
    delta = convertMarkdownToDelta(cleanedUp);
  } catch (e) {
    console.warn(e);
    delta = { insert: cleanedUp };
  }

  return { ops: delta };
};
